import { useAuth0 } from "@auth0/auth0-react";
import {
  CreateOrganizationPayload,
  MeData,
  MeResponse,
  MeSubscription,
  MeUsage,
  Organization,
  UpdateUserInfoPayload,
} from "@cohesive/api-client";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

export function useUserInfo(
  options?: Partial<UseQueryOptions<MeData, AxiosError>>,
) {
  const { isAuthenticated } = useAuth0();

  return useQuery<MeData, AxiosError>({
    queryKey: ["userInfo"],
    queryFn: () => orgAPI.user.show(),
    enabled: isAuthenticated,
    ...options,
  });
}

export function useUserTimezone() {
  const { data: userInfo } = useUserInfo();
  return userInfo?.organization_user?.metadata?.time_zone;
}

export function useUpdateUserInfo(
  options?: UseMutationOptions<
    MeResponse["data"],
    AxiosError,
    UpdateUserInfoPayload
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<MeResponse["data"], AxiosError, UpdateUserInfoPayload>({
    mutationFn: (payload) => orgAPI.user.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["userInfo"],
      });
    },
    ...options,
  });
}

export function useChangePassword(
  options?: UseMutationOptions<void, AxiosError>,
) {
  return useMutation<void, AxiosError>({
    mutationFn: () => orgAPI.user.changePassword(),
    ...options,
  });
}

export function useCreateOrganization(
  options?: UseMutationOptions<
    Organization,
    AxiosError,
    CreateOrganizationPayload
  >,
) {
  return useMutation<Organization, AxiosError, CreateOrganizationPayload>({
    mutationFn: async (payload) => {
      const organization = await orgAPI.organization.create(payload);

      return organization;
    },
    ...options,
  });
}

export type UsageLimitReason =
  | "none" // No usage limit
  | "trial_credits_exceeded" // Trial user exceeded included credits
  | "overage_limit_exceeded" // Active subscription exceeded overage spend limit
  | "unknown"; // Other usage restriction

export function classifyUsageLimitReason(
  subscription: MeSubscription,
  usage: MeUsage,
): UsageLimitReason {
  // A blocked trial is always a credit block: the per-user count trails the
  // org-wide one that actually tripped the limit.
  if (subscription.status === "trialing") return "trial_credits_exceeded";

  if (subscription.status === "active") {
    const overage = usage.current_period_org_credit_overage_count || 0;
    const maxOverageSpend =
      subscription.max_credit_overage_spend_per_period || 0;
    // $0 limit means no on-demand spending allowed.
    if (maxOverageSpend === 0 && overage > 0) return "overage_limit_exceeded";
    // -1 means unlimited, so there is no limit to exceed.
    if (maxOverageSpend > 0 && overage >= maxOverageSpend)
      return "overage_limit_exceeded";
  }

  return "unknown";
}

export function useUsageLimitReason(): {
  isUsageAllowed: boolean;
  reason: UsageLimitReason;
  hasPaymentMethod: boolean;
  isLoading: boolean;
  error: AxiosError | null;
} {
  const { data: userInfo, isLoading, error } = useUserInfo();

  const subscription = userInfo?.subscription;
  const usage = userInfo?.usage;

  const hasPaymentMethod = Boolean(subscription?.default_payment_method_id);

  // If loading or no subscription/usage data, assume no limit
  if (isLoading || !subscription || !usage) {
    return {
      isUsageAllowed: usage?.usage_allowed ?? true,
      reason: "none" as UsageLimitReason,
      hasPaymentMethod,
      isLoading,
      error,
    };
  }

  const isUsageAllowed = usage?.usage_allowed ?? true;

  // If usage is allowed, no limit
  if (isUsageAllowed) {
    return {
      isUsageAllowed,
      reason: "none" as UsageLimitReason,
      hasPaymentMethod,
      isLoading,
      error,
    };
  }

  return {
    isUsageAllowed,
    reason: classifyUsageLimitReason(subscription, usage),
    hasPaymentMethod,
    isLoading,
    error,
  };
}

export function useCurrentUsageLimit() {
  const { data: userInfo, isLoading, error } = useUserInfo();

  const subscription = userInfo?.subscription;
  const usage = userInfo?.usage;

  return {
    isUsageAllowed: usage?.usage_allowed ?? true, // Default to true if no usage data
    isLoading,
    error,
    billing:
      subscription && usage
        ? {
            planName: subscription.plan_name,
            planDisplayName: subscription.plan_display_name,
            subscriptionStatus: subscription.status,
            currentPeriodUserCreditCount:
              usage.current_period_user_credit_count,
            currentPeriodOrgCreditOverageCount:
              usage.current_period_org_credit_overage_count,
            includedCreditsPerSeat: subscription.included_credits_per_seat,
            maxCreditOverageSpendPerPeriod:
              subscription.max_credit_overage_spend_per_period,
            creditOverageLimitPerPeriod:
              subscription.credit_overage_limit_per_period,
            hasPaymentMethod: Boolean(subscription.default_payment_method_id),
            currentPeriodStart: subscription.current_period_start,
            currentPeriodEnd: subscription.current_period_end,
          }
        : undefined, // Change null to undefined to match expected type
  };
}
